import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Loader from "../../components/ui/Loader";
import { MoneyhopLogo } from "../../assets/Icons";
import { verifyQuoteApi } from "../../services/verifyQuote";
import { logApiErrorToSentry } from "../remittance-beta/utils/helper";
import QuoteSummary from "./components/QuoteSummary";
import OtpVerification, { QuoteVerifiedPanel } from "./components/OtpVerification";
import {
  hasQuoteSummaryData,
  QuoteVerifyInitResponse,
  QuoteVerifyOtpResponse,
} from "./types";
import { getQuoteSentDate } from "./utils";

type PageStatus = "loading" | "ready" | "verified" | "not_found" | "expired" | "error";

const getErrorMessage = (error: any) =>
  error?.response?.data?.error?.type ||
  error?.response?.error?.type ||
  error?.response?.error ||
  error?.message ||
  "Something went wrong. Please try again.";

const applyQuoteResponse = (
  data: QuoteVerifyInitResponse
): { status: PageStatus; message?: string } => {
  if (data.linkExpired) {
    return {
      status: "expired",
      message: data.message || "Link is expired, please check with sales person.",
    };
  }

  if (data.quoteVerified) {
    return {
      status: "verified",
      message: data.message || "Quote already verified.",
    };
  }

  return { status: "ready" };
};

const VerifyQuotePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const uniqueKey = searchParams.get("id") ?? "";

  const [pageStatus, setPageStatus] = useState<PageStatus>("loading");
  const [statusMessage, setStatusMessage] = useState<string>();
  const [quote, setQuote] = useState<QuoteVerifyInitResponse | null>(null);
  const [verifiedDetails, setVerifiedDetails] = useState<QuoteVerifyOtpResponse | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpError, setOtpError] = useState(false);
  const [verifyOtpError, setVerifyOtpError] = useState("");
  const [isOTPVerified, setIsOTPVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [sendOtpLoading, setSendOtpLoading] = useState(false);

  const loadQuote = useCallback(async () => {
    if (!uniqueKey) {
      setPageStatus("not_found");
      return;
    }

    setPageStatus("loading");
    setOtpSent(false);
    setOtpValue("");
    setOtpError(false);
    setVerifyOtpError("");

    try {
      const response = await verifyQuoteApi.getVerifyQuote(uniqueKey, "data");
      const data = response?.data;

      if (!data) {
        setPageStatus("error");
        return;
      }

      setQuote(data);
      const { status, message } = applyQuoteResponse(data);
      setStatusMessage(message);
      setPageStatus(status);

      if (status === "verified") {
        setVerifiedDetails({
          uniqueKey: data.uniqueKey || uniqueKey,
          quoteVerified: true,
          quoteVerifiedDate: data.quoteVerifiedDate,
          createdOn: data.createdOn,
          destCurrency: data.destCurrency,
          userSend: data.userSend,
          totalPay: data.totalPay,
        });
      }
    } catch (error: any) {
      const httpStatus = error?.response?.status;
      logApiErrorToSentry(error, "verifyQuoteApi.getVerifyQuote:data");

      if (httpStatus === 404) {
        setPageStatus("not_found");
        return;
      }
      if (httpStatus === 410) {
        setPageStatus("expired");
        setStatusMessage("Link is expired, please check with sales person.");
        return;
      }

      setPageStatus("error");
      toast.error(getErrorMessage(error));
    }
  }, [uniqueKey]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  const handleSendOtp = async () => {
    if (!uniqueKey) return;

    setSendOtpLoading(true);
    try {
      const response = await verifyQuoteApi.getVerifyQuote(uniqueKey, "otp");
      const data = response?.data;

      if (!data) {
        toast.error("Unable to send OTP. Please try again.");
        return;
      }

      setQuote((prev) => ({ ...(prev ?? {}), ...data }));

      if (data.linkExpired) {
        setPageStatus("expired");
        setStatusMessage(data.message || "Link is expired, please check with sales person.");
        return;
      }

      if (data.quoteVerified) {
        setPageStatus("verified");
        setStatusMessage(data.message || "Quote already verified.");
        setVerifiedDetails({
          uniqueKey: data.uniqueKey || uniqueKey,
          quoteVerified: true,
          quoteVerifiedDate: data.quoteVerifiedDate,
          createdOn: data.createdOn,
          destCurrency: data.destCurrency,
          userSend: data.userSend,
          totalPay: data.totalPay,
        });
        return;
      }

      if (data.otpSent) {
        setOtpSent(true);
        setOtpValue("");
        setOtpError(false);
        setVerifyOtpError("");
        if (data.message) {
          toast.success(data.message);
        }
        return;
      }

      toast.error(data.message || "Unable to send OTP. Please try again.");
    } catch (error: any) {
      const httpStatus = error?.response?.status;
      logApiErrorToSentry(error, "verifyQuoteApi.getVerifyQuote:otp");

      if (httpStatus === 404) {
        setPageStatus("not_found");
        return;
      }
      if (httpStatus === 410) {
        setPageStatus("expired");
        setStatusMessage("Link is expired, please check with sales person.");
        return;
      }

      toast.error(getErrorMessage(error));
    } finally {
      setSendOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!uniqueKey || otpValue.length !== 6) return;

    setOtpLoading(true);
    setOtpError(false);
    setVerifyOtpError("");

    try {
      const response = await verifyQuoteApi.verifyQuoteOtp(uniqueKey, otpValue);
      const data = response?.data;

      if (data?.quoteVerified) {
        setIsOTPVerified(true);
        setVerifiedDetails(data);
        setQuote((prev) => (prev ? { ...prev, ...data, quoteVerified: true } : prev));
        setPageStatus("verified");
        setStatusMessage("Quote verified successfully.");
        toast.success("Quote verified successfully.");
        return;
      }

      setOtpError(true);
      setVerifyOtpError("Invalid OTP. Please try again.");
    } catch (error: any) {
      setOtpError(true);
      setVerifyOtpError(getErrorMessage(error));
      logApiErrorToSentry(error, "verifyQuoteApi.verifyQuoteOtp");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!uniqueKey) return;

    try {
      const response = await verifyQuoteApi.resendVerifyQuoteOtp(uniqueKey);
      const message = response?.data?.message;
      toast.success(message || "OTP resent successfully.");
    } catch (error: any) {
      toast.error(getErrorMessage(error));
      logApiErrorToSentry(error, "verifyQuoteApi.resendVerifyQuoteOtp");
    }
  };

  const renderStatusMessage = () => {
    if (pageStatus === "not_found") {
      return (
        <div className="text-center max-w-md">
          <h2 className="text-xl font-bold text-[#111] mb-2">Quote not found</h2>
          <p className="text-sm text-[#666]">
            The quote link you followed is invalid or no longer exists.
          </p>
        </div>
      );
    }

    if (pageStatus === "expired") {
      return (
        <div className="text-center max-w-md">
          <h2 className="text-xl font-bold text-[#111] mb-2">Link expired</h2>
          <p className="text-sm text-[#666]">
            {statusMessage || "This verification link has expired. Please contact your sales person."}
          </p>
        </div>
      );
    }

    if (pageStatus === "error") {
      return (
        <div className="text-center max-w-md">
          <h2 className="text-xl font-bold text-[#111] mb-2">Unable to load quote</h2>
          <p className="text-sm text-[#666] mb-4">
            We could not load your quote details right now. Please try again.
          </p>
          <button
            type="button"
            onClick={loadQuote}
            className="px-6 py-2 rounded bg-[#0F5BF1] text-white text-sm font-semibold"
          >
            Retry
          </button>
        </div>
      );
    }

    return null;
  };

  const showQuoteSummary = quote && hasQuoteSummaryData(quote);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E0F2FF] via-[#F7FAFC] to-[#F7FAFC]">
      <ToastContainer autoClose={3000} position="top-right" />

      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex justify-center mb-10">
          <MoneyhopLogo />
        </div>

        {pageStatus === "loading" ? (
          <div className="flex justify-center py-20">
            <Loader />
          </div>
        ) : null}

        {["not_found", "expired", "error"].includes(pageStatus) ? (
          <div className="flex justify-center py-20">{renderStatusMessage()}</div>
        ) : null}

        {quote && ["ready", "verified"].includes(pageStatus) ? (
          <div
            className={`grid grid-cols-1 gap-8 w-full mx-auto items-stretch ${
              showQuoteSummary ? "lg:grid-cols-2 max-w-[1040px]" : "max-w-[520px]"
            }`}
          >
            {showQuoteSummary ? <QuoteSummary quote={quote} /> : null}

            {pageStatus === "verified" ? (
              <QuoteVerifiedPanel
                message={statusMessage}
                createdOn={getQuoteSentDate(quote, verifiedDetails)}
                quoteVerifiedDate={
                  verifiedDetails?.quoteVerifiedDate ?? quote.quoteVerifiedDate ?? undefined
                }
              />
            ) : (
              <OtpVerification
                maskedContact={quote.maskedContact}
                sendType={quote.sendType}
                smsType={quote.smsType}
                otpSent={otpSent}
                otpValue={otpValue}
                setOtpValue={setOtpValue}
                otpError={otpError}
                verifyOtpError={verifyOtpError}
                isOTPVerified={isOTPVerified}
                isLoading={otpLoading}
                sendOtpLoading={sendOtpLoading}
                onSendOtp={handleSendOtp}
                onVerify={handleVerifyOtp}
                onResend={handleResendOtp}
              />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default VerifyQuotePage;
